import { MessageKey } from "@/i18n";
import { CategoryGender } from "@/types";
import {
  ExternalLicenseMatchMode,
  SkillLevel,
  SortBy,
  SortOrder,
  TournamentCategoriesTableColumn,
  TournamentCategoryAgeGroup,
  TournamentCategoryAgeLimitDirection,
  TournamentCategoryDefaultSorting,
  TournamentCategoryRatingSystem,
  TournamentCategoryStatus,
  TournamentCategoryType,
  WprRatingType,
  WprRequiredStatusRating,
} from "@/types/tournamentCategory.types";
import { BadgeColor, PointIndicatorColor } from "@/ui";

/**
 * Query-key prefix for the tournament-categories list. Unlike the other list
 * query-key helpers, `getDashboardTournamentCategoriesControllerListQueryKey`
 * requires `params` (leagueId/page/limit/keyword), so we can't precompute a
 * single concrete key. React Query matches by prefix, so invalidating this URL
 * segment invalidates every paginated variant of the list.
 */
export const TOURNAMENT_CATEGORIES_LIST_QUERY_KEY_PREFIX = [
  "/api/dashboard/v1/tournaments/categories",
] as const;

/**
 * Query-key prefix for the tournament-category-groups list. Like the categories
 * prefix above, the generated list query-key helper requires `params`, so we
 * match by URL prefix — invalidating this segment refreshes every paginated
 * variant of the groups list. Category mutations (status / delete / duplicate)
 * happen inside the group accordions too, so they invalidate this alongside the
 * categories list.
 */
export const TOURNAMENT_CATEGORY_GROUPS_LIST_QUERY_KEY_PREFIX = [
  "/api/dashboard/v1/tournament-category-groups",
] as const;

export const TOURNAMENT_CATEGORIES_INVALIDATE_KEYS = [
  TOURNAMENT_CATEGORIES_LIST_QUERY_KEY_PREFIX,
  TOURNAMENT_CATEGORY_GROUPS_LIST_QUERY_KEY_PREFIX,
];

export const TOURNAMENT_CATEGORY_STATUS_LABELS: {
  [key in TournamentCategoryStatus]: MessageKey;
} = {
  Active: "league.tournamentCategories.status.active",
  Inactive: "league.tournamentCategories.status.inactive",
};

export const TOURNAMENT_CATEGORY_TYPE_LABELS: { [key in TournamentCategoryType]: MessageKey } = {
  single: "league.tournamentCategories.type.single",
  doubles: "league.tournamentCategories.type.doubles",
  team: "league.tournamentCategories.type.team",
};

export const TOURNAMENT_CATEGORY_GENDER_LABELS: { [key in CategoryGender]: MessageKey } = {
  male: "league.tournamentCategories.gender.male",
  female: "league.tournamentCategories.gender.female",
  anyone: "league.tournamentCategories.gender.anyone",
  mixed: "league.tournamentCategories.gender.mixed",
};

export const TOURNAMENT_CATEGORY_STATUS_BADGE_COLORS: {
  [key in TournamentCategoryStatus]: BadgeColor;
} = {
  Active: "positive",
  Inactive: "danger",
};

export const TOURNAMENT_CATEGORY_STATUS_POINT_INDICATOR_COLORS: {
  [key in TournamentCategoryStatus]: PointIndicatorColor;
} = {
  Active: "positive",
  Inactive: "danger",
};

export const TOURNAMENT_CATEGORIES_TABLE_COLUMN_LABELS: {
  [key in TournamentCategoriesTableColumn]: MessageKey;
} = {
  id: "general.columns.id",
  title: "league.tournamentCategories.columns.title",
  type: "league.tournamentCategories.columns.type",
  gender: "general.columns.gender",
  tournaments: "league.tournamentCategories.columns.tournaments",
  status: "general.columns.status",
};

export const TOURNAMENT_CATEGORY_SKILL_LEVEL_LABELS: { [key in SkillLevel]: MessageKey } = {
  OPEN: "league.tournamentCategories.enums.skillLevel.open",
  BEGINNER: "league.tournamentCategories.enums.skillLevel.beginner",
  LOWER_INTER: "league.tournamentCategories.enums.skillLevel.lowerInter",
  INTERMEDIATE: "league.tournamentCategories.enums.skillLevel.intermediate",
  ADVANCED: "league.tournamentCategories.enums.skillLevel.advanced",
  PRO: "league.tournamentCategories.enums.skillLevel.pro",
};

export const TOURNAMENT_CATEGORY_AGE_GROUP_LABELS: {
  [key in TournamentCategoryAgeGroup]: MessageKey;
} = {
  seniors: "league.tournamentCategories.enums.ageGroup.seniors",
  adults: "league.tournamentCategories.enums.ageGroup.adults",
  juniors: "league.tournamentCategories.enums.ageGroup.juniors",
  kids: "league.tournamentCategories.enums.ageGroup.kids",
};

export const TOURNAMENT_CATEGORY_AGE_LIMIT_DIRECTION_LABELS: {
  [key in TournamentCategoryAgeLimitDirection]: MessageKey;
} = {
  above: "league.tournamentCategories.enums.ageLimitDirection.above",
  under: "league.tournamentCategories.enums.ageLimitDirection.under",
};

export const TOURNAMENT_CATEGORY_RATING_SYSTEM_LABELS: {
  [key in TournamentCategoryRatingSystem]: MessageKey;
} = {
  wpr: "league.tournamentCategories.enums.ratingSystem.wpr",
  dupr: "league.tournamentCategories.enums.ratingSystem.dupr",
  cpc: "league.tournamentCategories.enums.ratingSystem.cpc",
  maxwill: "league.tournamentCategories.enums.ratingSystem.maxwill",
};

export const EXTERNAL_LICENSE_MATCH_MODE_LABELS: {
  [key in ExternalLicenseMatchMode]: MessageKey;
} = {
  all: "league.tournamentCategories.enums.externalLicenseMatchMode.all",
  anyone: "league.tournamentCategories.enums.externalLicenseMatchMode.anyone",
};

export const TOURNAMENT_CATEGORY_WPR_RATING_TYPE_LABELS: {
  [key in WprRatingType]: MessageKey;
} = {
  COMPETITION: "league.tournamentCategories.enums.wprRatingType.COMPETITION",
  SOCIAL: "league.tournamentCategories.enums.wprRatingType.SOCIAL",
};

export const TOURNAMENT_CATEGORY_WPR_REQUIRED_STATUS_RATING_LABELS: {
  [key in WprRequiredStatusRating]: MessageKey;
} = {
  rated: "league.tournamentCategories.enums.wprRequiredStatusRating.rated",
  temporary: "league.tournamentCategories.enums.wprRequiredStatusRating.temporary",
  both: "league.tournamentCategories.enums.wprRequiredStatusRating.both",
};

export const TOURNAMENT_CATEGORY_DEFAULT_SORTING_LABELS: {
  [key in TournamentCategoryDefaultSorting]: MessageKey;
} = {
  totalPointsDesc: "league.tournamentCategories.enums.defaultSorting.totalPointsDesc",
  totalRankingsAsc: "league.tournamentCategories.enums.defaultSorting.totalRankingsAsc",
  totalWprAvgDesc: "league.tournamentCategories.enums.defaultSorting.totalWprAvgDesc",
  totalDuprDesc: "league.tournamentCategories.enums.defaultSorting.totalDuprDesc",
  registrationDateAsc: "league.tournamentCategories.enums.defaultSorting.registrationDateAsc",
};

/** Sort-direction suffix appended to a default-sorting option label (e.g. "Total points DESC"). */
export const TOURNAMENT_CATEGORY_SORT_ORDER_LABELS: { [key in SortOrder]: MessageKey } = {
  ASC: "league.tournamentCategories.enums.sortOrder.ASC",
  DESC: "league.tournamentCategories.enums.sortOrder.DESC",
};

/**
 * Maps each FE-combined default-sorting option to the `{ sortBy, sortOrder }`
 * pair the backend persists. Consumed by the inbound / outbound mappers in
 * `tournamentCategory.mappers.ts`.
 */
export const TOURNAMENT_CATEGORY_DEFAULT_SORTING_PAIRS: {
  [key in TournamentCategoryDefaultSorting]: { sortBy: SortBy; sortOrder: SortOrder };
} = {
  totalPointsDesc: { sortBy: "POINTS", sortOrder: "DESC" },
  totalRankingsAsc: { sortBy: "RANKING", sortOrder: "ASC" },
  totalWprAvgDesc: { sortBy: "WPR", sortOrder: "DESC" },
  totalDuprDesc: { sortBy: "DUPR", sortOrder: "DESC" },
  registrationDateAsc: { sortBy: "REGISTRATION_DATE", sortOrder: "ASC" },
};

/**
 * Reverse of {@link TOURNAMENT_CATEGORY_DEFAULT_SORTING_PAIRS}: resolves a stored
 * `{ sortBy, sortOrder }` pair (keyed as `"sortBy:sortOrder"`) back to its FE
 * default-sorting option. Consumed by `mapToTournamentCategoryDefaultSorting` to
 * turn the ordered `sortingRules` wire array into the Tab-4 priority list.
 */
export const TOURNAMENT_CATEGORY_SORTING_RULE_TO_OPTION: Record<
  string,
  TournamentCategoryDefaultSorting
> = Object.fromEntries(
  (
    Object.keys(TOURNAMENT_CATEGORY_DEFAULT_SORTING_PAIRS) as TournamentCategoryDefaultSorting[]
  ).map((option) => {
    const { sortBy, sortOrder } = TOURNAMENT_CATEGORY_DEFAULT_SORTING_PAIRS[option];
    return [`${sortBy}:${sortOrder}`, option];
  })
);

/**
 * Machine-readable error codes the tournament-category BULK endpoints answer
 * with. Declared here rather than read off `@/api/__generated__/schemas` because
 * the endpoints landed after the last `yarn rest:codegen` run — same reason
 * `useTournamentCategoryBulkMutations` and `useBulkUpdateTournamentCategories`
 * are hand-written.
 *
 * TODO(codegen): swap for the generated `…409AllOfErrorCode` /
 * `…400AllOfErrorCode` enums once the spec is regenerated. This is the single
 * place that needs updating when it is.
 */
export const TOURNAMENT_CATEGORY_BULK_ERROR_CODES = {
  /** 409 from `bulk-delete`: at least one category still has entries or ranking links. */
  inUse: "TOURNAMENT_CATEGORY_IN_USE",
  /** 400 from `copy`: a category's sport isn't offered by the target league. */
  copySportMismatch: "TOURNAMENT_CATEGORY_COPY_SPORT_MISMATCH",
  /**
   * 409 from `bulk-update`: at least one selected category already has
   * registrations, so its `type` (format) can't be changed. All-or-nothing —
   * nothing in the batch was written.
   */
  typeChangeConflict: "TOURNAMENT_CATEGORY_TYPE_CHANGE_CONFLICT",
} as const;
